import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/axios";
import { Plus, LogOut } from "lucide-react";
import { toast } from "react-toastify";
import EntriesModal from "../../components/EntriesModal";
import ExitsModal from "../../components/ExitsModal";
import SearchBar from "../../components/SearchBar";
import LoadingOverlay from "../../components/LoadingOverlay";
import Pagination from "../../components/Pagination";

function Kardex() {
  const [kardex, setKardex] = useState([]);
  const [categories, setCategories] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
const [category, setCategory] = useState("");
  const [modalData, setModalData] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showEntriesModal, setShowEntriesModal] = useState(false);
  const [showExitsModal, setShowExitsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);

  const ITEMS_PER_PAGE = 50;
  const [currentPage, setCurrentPage] = useState(0);

  const monthNames = [
    "Enero","Febrero","Marzo","Abril","Mayo","Junio",
    "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
  ];

  /* ===================== DATA ===================== */

  useEffect(() => {
    fetchCategories();
    fetchYears();
  }, []);

useEffect(() => {
  fetchKardex(); // Llamamos solo al montar el componente la primera vez
}, []);

  const fetchYears = async () => {
    try {
      const res = await api.get("/kardex/years");
      setAvailableYears(res.data);
      if (!res.data.includes(year)) {
        setYear(res.data[res.data.length - 1]);
      }
    } catch {
      toast.error("Error al cargar años");
    }
  };

  const handleFilter = () => {
    fetchKardex();
  };


  const fetchCategories = async () => {
    try {
      const res = await api.get("/kardex/categories");
      setCategories(res.data);
    } catch {
      toast.error("Error al cargar categorías");
    }
  };

  const fetchKardex = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/kardex?year=${year}&month=${month}&categoryId=${category}`
      );
      setKardex(res.data);
    } catch {
      toast.error("Error al cargar el kardex");
    } finally {
      setLoading(false);
    }
  };

  /* ===================== FILTRO + PAGINACIÓN ===================== */

  const filteredKardex = useMemo(() => {
    return kardex.filter(item =>
      item.product.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [kardex, searchTerm]);

  const pageCount = Math.ceil(filteredKardex.length / ITEMS_PER_PAGE);

  const currentItems = useMemo(() => {
    return filteredKardex.slice(
      currentPage * ITEMS_PER_PAGE,
      (currentPage + 1) * ITEMS_PER_PAGE
    );
  }, [filteredKardex, currentPage]);

  // 🔥 Resetear página cuando cambia búsqueda o filtros
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, month, year, category]);

  /* ===================== MODALES ===================== */

  const fetchDetails = async (type, productId, productName) => {
    try {
      const res = await api.get(
        `/kardex/${type}?productId=${productId}&year=${year}&month=${month}`
      );

      setModalData(res.data);

      setModalTitle(
        `${type === "entries" ? "Entradas" : "Salidas"} - ${
          productName
        } (${monthNames[month - 1]} ${year})`
      );

      setShowEntriesModal(type === "entries");
      setShowExitsModal(type === "exits");
      setShowModal(true);
    } catch {
      toast.error("Error al cargar los detalles");
    }
  };

  /* ===================== UI ===================== */

const handleExportExcel = async () => {
  try {
    const res = await api.get("/export/kardex/excel", { // Ruta relativa
      params: {
        year,
        month,
        categoryId: category === "all" ? "" : category, // Limpieza de filtro
        search: searchTerm,
      },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `kardex_${month}_${year}.xlsx`;
    document.body.appendChild(a); // Recomendado para Firefox
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error(error);
    toast.error("Error al exportar Kardex");
  }
};



  return (
    <div className="bg-white p-6 rounded-xl shadow-md relative">
      <LoadingOverlay loading={loading} />

      <div className="flex items-center justify-between mb-6">
  <h2 className="text-3xl font-semibold text-gray-800">Kardex</h2>

  <div className="flex items-center gap-3">
    <SearchBar
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      placeholder="Buscar por producto..."
    />
          <button
            onClick={handleExportExcel}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Excel
          </button>

    <select value={month} onChange={(e) => setMonth(+e.target.value)} className="border rounded p-2">
      {monthNames.map((name, i) => (
        <option key={i + 1} value={i + 1}>{name}</option>
      ))}
    </select>

    <select value={year} onChange={(e) => setYear(+e.target.value)} className="border rounded p-2">
      {availableYears.map((y) => (
        <option key={y} value={y}>{y}</option>
      ))}
    </select>

    <select value={category} onChange={(e) => setCategory(e.target.value)} className="border rounded p-2">
      <option value="all">Todas las Categorías</option>
      {categories.map((cat) => (
        <option key={cat.id} value={cat.id}>{cat.name}</option>
      ))}
    </select>

    {/* Contenedor del botón */}
    <div className="ml-2">
      <button
        onClick={handleFilter}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
      >
        Filtrar
      </button>
    </div>
  </div>
</div>


      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-sm uppercase text-center">
              <th className="px-6 py-3">Insumo</th>
              <th className="px-6 py-3">Inicio Mes</th>
              <th className="px-6 py-3">Ingresos</th>
              <th className="px-6 py-3">Egresos</th>
              <th className="px-6 py-3">Saldo</th>
              <th className="px-6 py-3 w-28">Acciones</th>
            </tr>
          </thead>

          <tbody className="text-center">
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-6 text-gray-400 italic">
                  No hay registros.
                </td>
              </tr>
            ) : (
              currentItems.map((item, index) => (
                <tr key={item.product.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="px-6 py-4">{item.product.name}</td>
                  <td className="px-6 py-4">{item.start_balance}</td>
                  <td className="px-6 py-4">{item.total_entries}</td>
                  <td className="px-6 py-4">{item.total_exits}</td>
                  <td className="px-6 py-4">{item.end_balance}</td>
                  <td className="px-6 py-4 flex justify-center gap-2">
                    <button
                      className="bg-green-500 text-white p-2 rounded-lg"
                      onClick={() => fetchDetails("entries", item.product.id, item.product.name)}
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      className="bg-red-500 text-white p-2 rounded-lg"
                      onClick={() => fetchDetails("exits", item.product.id, item.product.name)}
                    >
                      <LogOut size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINACIÓN */}
      {pageCount > 1 && (
       <Pagination
  pageCount={pageCount}
  currentPage={currentPage}
  onPageChange={setCurrentPage}
  currentItemsCount={currentItems.length}
  totalItemsCount={filteredKardex.length}
/>

      )}

  
      {showModal && showEntriesModal && (
        <EntriesModal data={modalData} title={modalTitle} onClose={() => setShowModal(false)} />
      )}
      {showModal && showExitsModal && (
        <ExitsModal data={modalData} title={modalTitle} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

export default Kardex;
